import React, { useState, useEffect } from "react";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Hourglass, Calendar, MapPin, Heart, Sparkles } from "lucide-react";

const fmtBytes = (b) => { const u=["B","KB","MB","GB","TB"]; let i=0,n=b||0; while(n>=1024&&i<u.length-1){n/=1024;i++} return `${n.toFixed(n>=100?0:1)} ${u[i]}`;};

export default function LegacyMode() {
  const { t, lang } = useLang();
  const [tx, setTx] = useState([]);
  const [caps, setCaps] = useState([]);

  useEffect(() => {
    api.get("/transmissions").then((r) => setTx(r.data)).catch(() => {});
    api.get("/capsules").then((r) => setCaps(r.data)).catch(() => {});
  }, []);

  // Build chronological items
  const items = [...tx.map((x) => ({
    type: "tx", id: x.transmission_id, name: x.capsule_name, when: x.started_at,
    sub: x.destination?.name || "—",
    meta: `${fmtBytes(x.package_size)} · ${x.status.replace(/_/g, " ")}`,
  })), ...caps.map((c) => ({
    type: "cap", id: c.id, name: c.capsule_name, when: c.created_at,
    sub: lang === "pt" ? "Cápsula curada" : "Curated capsule",
    meta: `${c.file_count} · ${fmtBytes(c.total_size)}`,
  }))].sort((a, b) => (b.when || "").localeCompare(a.when || ""));

  const eras = items.length > 0
    ? items.reduce((acc, item) => {
        const yr = (item.when || "").slice(0, 4) || "—";
        if (!acc[yr]) acc[yr] = [];
        acc[yr].push(item);
        return acc;
      }, {})
    : null;

  return (
    <AppShell>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-overline flex items-center gap-2"><Hourglass size={12} /> Multigenerational Archive</div>
          <h1 className="font-display font-black text-4xl tracking-tighter mt-2" data-testid="legacy-title">{t("legacy.title")}</h1>
          <p className="text-white/55 mt-2 max-w-2xl">{t("legacy.subtitle")}</p>
        </div>
        <div className="holo-panel px-4 py-3 flex items-center gap-3">
          <Sparkles size={14} className="text-[#00D4FF]" />
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/70">
            {items.length} {lang === "pt" ? "artefatos preservados" : "artifacts preserved"}
          </div>
        </div>
      </div>

      {!eras ? (
        <div className="holo-panel p-12 text-center" data-testid="legacy-empty">
          <Heart size={28} className="mx-auto text-[#00D4FF] float" />
          <div className="font-display font-black text-2xl tracking-tighter mt-6">
            {lang === "pt"
              ? "Sua linha do tempo começa com a primeira transmissão."
              : "Your legacy timeline begins with your first transmission."}
          </div>
          <div className="font-mono-data text-[11px] tracking-[0.18em] uppercase text-white/55 mt-4">
            {lang === "pt"
              ? "Vozes, fotos, vídeos e mensagens — preservadas além da Terra."
              : "Voices, photos, videos and messages — preserved beyond Earth."}
          </div>
        </div>
      ) : (
        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-px bg-gradient-to-b from-[#00D4FF]/60 via-[#00D4FF]/20 to-transparent" />
          {Object.entries(eras).map(([year, list]) => (
            <div key={year} className="relative mb-10">
              <div className="absolute -left-6 top-1.5 w-3 h-3 bg-[#00D4FF] rounded-full pulse-glow" />
              <div className="label-overline flex items-center gap-2 text-[#00D4FF]">
                <Calendar size={12} /> {year}
              </div>
              <div className="mt-4 grid md:grid-cols-2 gap-4">
                {list.map((it) => (
                  <div key={it.type + it.id} className="holo-panel p-5" data-testid={`legacy-item-${it.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="label-overline">{it.type === "tx" ? (lang === "pt" ? "Transmissão" : "Transmission") : (lang === "pt" ? "Cápsula" : "Capsule")}</div>
                      <MapPin size={12} className="text-[#00D4FF]" />
                    </div>
                    <div className="font-display font-bold text-lg mt-2 tracking-tight">{it.name}</div>
                    <div className="font-mono-data text-[11px] tracking-[0.16em] uppercase text-white/55 mt-2">{it.sub}</div>
                    <div className="font-mono-data text-[10px] tracking-[0.14em] uppercase text-white/40 mt-1">{it.meta}</div>
                    <div className="font-mono-data text-[10px] text-white/40 mt-2">{(it.when || "").slice(0, 16).replace("T", " ")}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
